import React, { useState } from 'react';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import './MiniFileUploader.css';

const MiniFileUploader = ({ onFileUploaded, acceptedTypes = "application/pdf,.doc,.docx,.ppt,.pptx", placeholder = "Upload document" }) => {
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const auth = getAuth();

    const handleFileSelect = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file size (10MB limit for documents)
        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
            alert('File size too large. Maximum size is 10MB');
            return;
        }

        setUploading(true);
        setProgress(0);

        try {
            const storage = getStorage();
            const userId = auth.currentUser?.uid;
            const timestamp = Date.now();
            const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
            const storageRef = ref(storage, `course-documents/${userId}/${timestamp}_${sanitizedFileName}`);

            const uploadTask = uploadBytesResumable(storageRef, file);

            uploadTask.on('state_changed', 
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setProgress(Math.round(progress));
                }, 
                (error) => {
                    console.error('Upload error:', error);
                    alert('Upload failed. Please try again.');
                    setUploading(false);
                    setProgress(0);
                }, 
                async () => {
                    try {
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        onFileUploaded({
                            url: downloadURL,
                            name: file.name,
                            size: file.size,
                            type: file.type
                        });
                        setUploading(false);
                        setProgress(0);
                    } catch (error) {
                        console.error('Error getting download URL:', error);
                        alert('Upload completed but failed to get file URL. Please try again.');
                        setUploading(false);
                        setProgress(0);
                    }
                }
            );
        } catch (error) {
            console.error('Upload error:', error);
            alert('Upload failed. Please try again.');
            setUploading(false);
            setProgress(0);
        }
    };

    return (
        <div className="sales-mini-file-uploader">
            <input
                type="file"
                accept={acceptedTypes}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                id={`file-upload-${Math.random()}`}
                disabled={uploading}
            />
            
            {uploading ? (
                <div className="sales-mini-upload-progress">
                    <div className="sales-mini-progress-circle">
                        <svg width="20" height="20" viewBox="0 0 24 24">
                            <circle 
                                cx="12" 
                                cy="12" 
                                r="8" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeDasharray="50.27" 
                                strokeDashoffset={50.27 - (50.27 * progress) / 100}
                                transform="rotate(-90 12 12)"
                            />
                        </svg>
                    </div>
                    <span className="sales-mini-progress-text">{progress}%</span>
                </div>
            ) : (
                <label 
                    htmlFor={`file-upload-${Math.random()}`}
                    className="sales-mini-upload-button"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7,10 12,15 17,10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    <span>{placeholder}</span>
                </label>
            )}
        </div>
    );
};

export default MiniFileUploader;
